/**
 * Pure helpers for the Modify-Parties flow.
 *
 * Ported from the mediator portal so the case-manager wizard computes the SAME
 * numbers the mediator sees. The split maths in particular must match
 * byte-for-byte: scale-be stores whatever the FE sends, and the mediator portal
 * and this portal both write to the same invoices. A different rounding rule on
 * one side would make the two portals disagree about the same case.
 *
 * Sources (scale-portal):
 *   src/utils/utils.js                          — splitWhole, byClientIdAsc,
 *                                                 calculateStripeProcessingFee,
 *                                                 getPartyTypeByRole
 *   .../CaseDetail/Participants/AddNewPartyModal.jsx — the rest
 *
 * Everything here is pure: no React, no network. That keeps it unit-testable
 * and keeps the modal component about rendering.
 */

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

/** Currency (totals) — always 2 decimals, e.g. "315.00". */
export const cur = (n) =>
    (Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

/**
 * Money display — up to 2 decimals, but HIDE ".00" for whole numbers:
 * 333.33 -> "333.33", 500 -> "500" (never "500.00"). Used for mediation
 * amounts/subtotal/total now that the split is cent-accurate, so a $1000/3
 * share renders "333.34"/"333.33" instead of a rounded "333".
 */
export const fmtMoney = (n) => {
    const v = Number(n) || 0
    return v % 1 === 0
        ? v.toLocaleString('en-US')
        : v.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })
}

/** Fee — whole dollars show with no decimals ("250"); cents only when real. */
export const fmtFee = (n) => {
    const v = Number(n) || 0
    return Number.isInteger(v) ? v.toLocaleString() : cur(v)
}

/**
 * Split `total` into `n` SUM-PRESERVING shares, distributing the remainder one
 * cent at a time across the leading rows.
 *
 * This is the exact algorithm the mediator portal uses. It matters because the
 * backend RPC's own even split uses floor + all-remainder-to-first, which
 * differs by a cent — so the FE always sends explicit per-party amounts rather
 * than letting the backend re-derive them.
 */
export function splitWhole(total, n) {
    if (n <= 0) return []
    const cents = Math.round((Number(total) || 0) * 100)
    const base = Math.floor(cents / n)
    const rem = cents - base * n
    return Array.from({ length: n }, (_, i) => Math.round(base + (i < rem ? 1 : 0)) / 100)
}

/**
 * Canonical party ordering for splitWhole: client_id ascending, nulls last (a
 * brand-new party's client_id is created server-side, so it sorts after the
 * existing rows). Matches the backend's ORDER BY client_id, so the +1c
 * remainder lands on the same rows in the preview and in the stored bill.
 */
export function byClientIdAsc(a, b) {
    const ax = a?.client_id
    const bx = b?.client_id
    if (ax == null && bx == null) return 0
    if (ax == null) return 1
    if (bx == null) return -1
    if (typeof ax === 'number' && typeof bx === 'number') return ax - bx
    const as = String(ax)
    const bs = String(bx)
    return as < bs ? -1 : as > bs ? 1 : 0
}

/** Stripe's card fee, reconstructed with the same formula the backend uses. */
export const calculateStripeProcessingFee = (amount = 0, convertInCent = true, notAdd30Cent = false) => {
    let fee = notAdd30Cent ? amount * 0.029 : amount * 0.029 + 0.3
    fee = Math.floor(fee * 100) / 100
    return convertInCent ? Math.floor(fee * 100) : fee
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** Initials for an avatar (max 2 chars). */
export const initialsOf = (label) => {
    const words = String(label || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
    if (!words.length) return '?'
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** What a side is called on this case — ODR and coupon cases rename the sides. */
export const roleWord = ({ side, is_odr_mediator, isSystemCoupon }) => {
    const isP = side === 'plaintiff'
    if (is_odr_mediator) return isP ? 'Party 1' : 'Party 2'
    if (isSystemCoupon) return isP ? 'initiating party' : 'responding party'
    return isP ? 'plaintiff' : 'defendant'
}

export const getPartyTypeByRole = (
    role,
    position = null,
    is_odr_mediator = false,
    isSystemCoupon = false,
    is_car_case_not_scheduled = false
) => {
    if (role === 'plaintiff' || role === 'primary_plaintiff') {
        return is_car_case_not_scheduled || isSystemCoupon
            ? 'Initiating Party'
            : is_odr_mediator
              ? position && position > 1
                  ? `Party ${position + 1}`
                  : 'Party 1'
              : `Plaintiff${position ? ` ${position}` : ''}`
    }
    if (role === 'defendant' || role === 'primary_defendant') {
        return is_car_case_not_scheduled || isSystemCoupon
            ? 'Responding Party'
            : is_odr_mediator
              ? position && position > 1
                  ? `Party ${position + 1}`
                  : 'Party 2'
              : `Defendant${position ? ` ${position}` : ''}`
    }
    return ''
}

// ---------------------------------------------------------------------------
// Case gates
// ---------------------------------------------------------------------------

/**
 * Collect payment-like rows from case detail for the paid-invoice gate.
 *
 * HOURLY ROWS COUNT. On the mediator portal `plaintiffPayments` already
 * contains the party's hourly_invoice_payment rows, because scale-be composes
 * the array that way (adminController.js:818-861). This portal's backend now
 * composes them identically — but the `hourlyPayment` arrays are read here too,
 * so the gate is still correct against a backend that has not been deployed
 * yet. Re-reading rows the array already holds is a no-op: every consumer of
 * this list is a `.some()`.
 *
 * Getting this wrong is what left Add/Remove Party enabled on a paid hourly
 * case: the only row the gate could see was the $0 `payments` placeholder
 * written at booking, and it fails the `amount > 0` test below.
 */
export const collectCasePaymentsForGate = (caseData) => {
    if (!caseData) return []
    const out = []
    const push = (p) => {
        if (p && typeof p === 'object') out.push(p)
    }
    const pushAll = (rows) => {
        if (Array.isArray(rows)) rows.forEach(push)
    }
    pushAll(caseData.plaintiffPayments)
    pushAll(caseData.defendentPayments)
    push(caseData.plaintiff?.payment)
    push(caseData.defendant?.payment)
    pushAll(caseData.plaintiff?.hourlyPayment)
    pushAll(caseData.defendant?.hourlyPayment)
    const pushAdditional = (side) => {
        ;(side?.additionalParties || []).forEach((party) => {
            push(party?.payment)
            push(party?.client?.payment)
            pushAll(party?.hourlyPayment)
        })
    }
    pushAdditional(caseData.plaintiff)
    pushAdditional(caseData.defendant)
    return out
}

/**
 * Modifying parties is blocked once a REAL payment has been made. A $0
 * paid_manually row is a placeholder (hourly cases / mediator-absorbed parties
 * create these at booking) — it is NOT a real payment, so it must NOT lock the
 * case. Require amount > 0 (mirrors the backend orchestrator's paid-lock).
 */
export const hasPaidInvoiceOnCase = (caseData) =>
    collectCasePaymentsForGate(caseData).some(
        (p) => (p?.status === 'paid' || p?.status === 'paid_manually') && Number(p?.amount) > 0
    )

/**
 * At least one party has an ACTUALLY GENERATED invoice (invoice_id or
 * invoice_url is set). The "email the updated invoice" option only makes sense
 * when there is a real invoice to re-send, so it is gated on THIS — not on
 * hasInvoiceRecords, which can be true when the finances table shows only
 * amount rows with no invoice generated yet.
 */
export const hasGeneratedInvoice = (caseData) =>
    collectCasePaymentsForGate(caseData).some(
        (p) =>
            (p?.invoice_id != null && String(p.invoice_id).trim() !== '') ||
            (p?.invoice_url != null && String(p.invoice_url).trim() !== '')
    )

// ---------------------------------------------------------------------------
// Party collection
// ---------------------------------------------------------------------------

/**
 * The case's current paying parties + their mediation base and admin fee.
 *
 * `hourlyAmtByClient` / `hourlyAdminByClient` carry hourly-invoice amounts
 * keyed by client_id — see buildHourlyMaps below. Fixed cases pass empty maps
 * and never touch that path.
 */
export const collectExistingParties = (caseData, hourlyAmtByClient = {}, hourlyAdminByClient = {}) => {
    if (!caseData) return []
    const rows = []
    const cid = (p) => p?.client?.client_id ?? p?.client_id ?? null

    // Mediation (base) fee per party. The split is computed on the BASE only;
    // the admin fee is layered on per party afterwards — never lump them, or
    // the maths is wrong on re-splits. Fixed cases carry the base on
    // payment.amount (> 0). Hourly cases get a $0 paid_manually PLACEHOLDER and
    // the real amount lives on the hourly invoices.
    // A placeholder has payment.amount === 0 (NOT null), so a plain `??` would
    // not fall back — prefer the fixed amount only when it is > 0.
    const amt = (p) => {
        const fixed = p?.payment?.amount
        if (fixed != null && Number(fixed) > 0) return round2(fixed)
        const hourly = hourlyAmtByClient[cid(p)]
        if (hourly != null) return round2(hourly)
        return round2(Number(fixed) || 0)
    }

    // Admin fee per party — ALWAYS a whole dollar, so round it: a fractionally
    // re-divided grand_total (1000 - 750.33 = 249.67) must never surface a
    // decimal admin fee. Prefer application_fee (true admin, CC-fee-free); fall
    // back to grand_total - amount only for legacy rows lacking it (that
    // fallback WOULD fold in a card fee, so it is last-resort).
    const adminOf = (p) => {
        const appFee = p?.payment?.application_fee
        if (appFee != null && Number(appFee) > 0) return Math.round(Number(appFee))
        const fixedAdmin = Math.round(
            Math.max(0, (p?.payment?.grand_total ?? 0) - (p?.payment?.amount ?? 0))
        )
        if (fixedAdmin > 0) return fixedAdmin
        return Math.round(Number(hourlyAdminByClient[cid(p)]) || 0)
    }

    const label = (p, fallback) => p?.client?.name || p?.name || p?.email || fallback

    if (caseData.plaintiff) {
        rows.push({
            key: 'plaintiff',
            side: 'plaintiff',
            client_id: cid(caseData.plaintiff),
            label: label(caseData.plaintiff, 'Plaintiff'),
            roleLabel: 'Plaintiff 1',
            amount: amt(caseData.plaintiff),
            admin: adminOf(caseData.plaintiff),
        })
    }
    if (caseData.defendant) {
        rows.push({
            key: 'defendant',
            side: 'defendant',
            client_id: cid(caseData.defendant),
            label: label(caseData.defendant, 'Defendant'),
            roleLabel: 'Defendant 1',
            amount: amt(caseData.defendant),
            admin: adminOf(caseData.defendant),
        })
    }
    ;(caseData.plaintiff?.additionalParties || []).forEach((p, i) =>
        rows.push({
            key: `p-add-${p.id ?? i}`,
            side: 'plaintiff',
            client_id: cid(p),
            label: label(p, `Plaintiff ${i + 2}`),
            roleLabel: `Plaintiff ${i + 2}`,
            amount: amt(p),
            admin: adminOf(p),
        })
    )
    ;(caseData.defendant?.additionalParties || []).forEach((p, i) =>
        rows.push({
            key: `d-add-${p.id ?? i}`,
            side: 'defendant',
            client_id: cid(p),
            label: label(p, `Defendant ${i + 2}`),
            roleLabel: `Defendant ${i + 2}`,
            amount: amt(p),
            admin: adminOf(p),
        })
    )
    return rows
}

/**
 * The case's current parties as removable targets. Each entry carries enough
 * info to pick the right endpoint:
 *   - primary    -> DELETE /cases/:id/parties/primary/:side
 *   - additional -> DELETE /cases/:id/parties/:casePartyId
 */
export const collectRemovableParties = (caseData, is_odr_mediator = false, isSystemCoupon = false) => {
    if (!caseData) return []
    const rows = []
    const label = (p, fallback) => p?.client?.name || p?.name || p?.email || fallback
    // Sort additionals by the DB `position` ascending (same as the participants
    // table) and label them by position, so the remove list's numbering matches
    // the table exactly (e.g. "Defendant 3").
    const sortByPosition = (list) =>
        [...(list || [])].sort((a, b) => (Number(a?.position) || 0) - (Number(b?.position) || 0))

    if (caseData.plaintiff?.client_id) {
        rows.push({
            key: 'plaintiff-primary',
            side: 'plaintiff',
            isPrimary: true,
            casePartyId: null,
            client_id: caseData.plaintiff.client_id,
            label: label(caseData.plaintiff, 'Plaintiff'),
            roleLabel: 'Plaintiff 1',
        })
    }
    sortByPosition(caseData.plaintiff?.additionalParties).forEach((p, i) =>
        rows.push({
            key: `plaintiff-additional-${p.case_party_id ?? p.id}`,
            side: 'plaintiff',
            isPrimary: false,
            casePartyId: p.case_party_id ?? p.id,
            client_id: p.client?.client_id ?? p.client_id ?? null,
            label: label(p, `Plaintiff ${i + 2}`),
            roleLabel: getPartyTypeByRole(
                'plaintiff',
                positionNumber(p) ?? i + 2,
                is_odr_mediator,
                isSystemCoupon
            ),
        })
    )
    if (caseData.defendant?.client_id) {
        rows.push({
            key: 'defendant-primary',
            side: 'defendant',
            isPrimary: true,
            casePartyId: null,
            client_id: caseData.defendant.client_id,
            label: label(caseData.defendant, 'Defendant'),
            roleLabel: 'Defendant 1',
        })
    }
    sortByPosition(caseData.defendant?.additionalParties).forEach((p, i) =>
        rows.push({
            key: `defendant-additional-${p.case_party_id ?? p.id}`,
            side: 'defendant',
            isPrimary: false,
            casePartyId: p.case_party_id ?? p.id,
            client_id: p.client?.client_id ?? p.client_id ?? null,
            label: label(p, `Defendant ${i + 2}`),
            roleLabel: getPartyTypeByRole(
                'defendant',
                positionNumber(p) ?? i + 2,
                is_odr_mediator,
                isSystemCoupon
            ),
        })
    )
    return rows
}

/**
 * cm-be sends an additional party's `position` as a rendered label
 * ("Plaintiff 4"), while the mediator portal receives the raw integer. Accept
 * both so the role labels come out the same in either portal.
 */
export function positionNumber(party) {
    const raw = party?.position
    if (raw == null) return null
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
    const match = String(raw).match(/(\d+)\s*$/)
    return match ? Number(match[1]) : null
}

// ---------------------------------------------------------------------------
// Hourly invoices
// ---------------------------------------------------------------------------

/**
 * Build the per-client mediation-base / admin maps from hourly invoice rows.
 *
 * An hourly invoice's `amount` is the FULL charge and ALREADY INCLUDES the
 * admin fee (a $1,500 hourly invoice = $1,250 mediation + $250 admin). The
 * split is computed on the BASE with admin layered back on per party, so admin
 * is stripped out here — otherwise it is counted twice.
 *
 * Card rows also carry the per-payer Stripe processing fee, which must NEVER be
 * pooled or redistributed, so it is reconstructed and stripped too.
 */
export function buildHourlyMaps(rows) {
    const amtMap = {}
    const adminMap = {}
    ;(Array.isArray(rows) ? rows : []).forEach((r) => {
        const id = r?.client_id ?? r?.clients?.client_id
        if (id == null) return
        // Admin fee is ALWAYS whole dollars.
        const adm = Math.round(Number(r?.admin_fee || 0))
        const total = Number(r?.grand_total || r?.amount || 0)
        const isCard = String(r?.method_of_payment || '').toLowerCase() === 'card'
        let base
        if (isCard) {
            const approx = Math.round(((total - 0.3) / 1.029) * 100) / 100
            let preFee = approx
            for (let d = -2; d <= 2; d += 1) {
                const p = Math.round((approx + d / 100) * 100) / 100
                const fee = calculateStripeProcessingFee(p, false)
                if (Math.round((p + fee) * 100) === Math.round(total * 100)) {
                    preFee = p
                    break
                }
            }
            base = Math.round((preFee - adm) * 100) / 100
        } else {
            base = Math.round((total - adm) * 100) / 100
        }
        amtMap[id] = Math.max(0, base)
        adminMap[id] = adm
    })
    return { amtMap, adminMap }
}

/**
 * Every hourly invoice row on the case. cm-be attaches them per party as
 * `hourlyPayment`, so unlike the mediator portal there is no extra fetch —
 * they just have to be gathered back into one list.
 */
export function collectHourlyRows(caseData) {
    const out = []
    const push = (list) => {
        if (Array.isArray(list)) out.push(...list)
    }
    push(caseData?.plaintiff?.hourlyPayment)
    push(caseData?.defendant?.hourlyPayment)
    ;(caseData?.plaintiff?.additionalParties || []).forEach((p) => push(p?.hourlyPayment))
    ;(caseData?.defendant?.additionalParties || []).forEach((p) => push(p?.hourlyPayment))
    return out
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

// Same shape the mediator portal's Yup schema enforces, hand-rolled so this
// portal does not take on a validation dependency for one form. The messages
// are kept identical so both portals report the same thing.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** @returns {{name?: string, email?: string}} — empty object means valid. */
export function validateAddParty({ name, emails }) {
    const errors = {}
    if (!String(name || '').trim()) errors.name = 'Name is required'

    const list = (Array.isArray(emails) ? emails : [])
        .map((e) => (typeof e === 'string' ? e.trim() : e))
        .filter((e) => e !== '' && e != null)

    if (!list.length) {
        errors.email = 'At least one email is required'
    } else if (list.some((e) => !EMAIL_RE.test(String(e)))) {
        errors.email = 'Invalid email'
    } else {
        const lowered = list.map((e) => String(e).toLowerCase())
        if (new Set(lowered).size !== lowered.length) errors.email = 'Duplicate email detected'
    }
    return errors
}

/** API / validation email messages → structured UI. */
export const parseEmailFieldError = (message) => {
    if (!message || typeof message !== 'string') return null
    const multi = message.match(/^These emails are already used on this case:\s*(.+)\.\s*$/i)
    if (multi) {
        const emails = multi[1]
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
        if (emails.length) return { kind: 'case_conflict', emails }
    }
    const single = message.match(/^(.+?)\s+is already used by a participant on this case\.\s*$/i)
    if (single) {
        const addr = single[1].trim()
        if (addr && !/\s/.test(addr)) return { kind: 'case_conflict', emails: [addr] }
    }
    return { kind: 'plain', message }
}

// ---------------------------------------------------------------------------
// Removal guards
// ---------------------------------------------------------------------------

/**
 * Why this party cannot be removed — '' when it can.
 *
 * These mirror rules the backend enforces, checked here so the reason is shown
 * BEFORE the user reaches the confirm step instead of arriving as a raw
 * database message after submitting.
 *
 * 1. LAST OF SIDE. A case must keep at least one party per side, so a primary
 *    can only be removed when there is another party of that side to promote.
 *
 * 2. SHARED CLIENT RECORD. An additional party can be created against an
 *    EXISTING client row when its email already belongs to someone on the case.
 *    When that row happens to be the case's own plaintiff_id / defender_id, the
 *    `remove_case_additional_party` RPC refuses with "Cannot remove primary
 *    party from case" — its guard compares the removed party's client_id
 *    against both primaries, and cannot tell the two apart. The mediator portal
 *    does not check this and lets the user hit the raw error; this portal
 *    explains it up front.
 */
export function removalBlockedReason(party, caseData, sideLabels = {}) {
    if (!party) return 'Select a party to remove.'

    const nameOf = (side) =>
        sideLabels[side] || (side === 'plaintiff' ? 'Plaintiff' : 'Defendant')

    if (party.isPrimary) {
        const sideData = party.side === 'defendant' ? caseData?.defendant : caseData?.plaintiff
        const hasPromotable = Array.isArray(sideData?.additionalParties)
            ? sideData.additionalParties.length > 0
            : false
        if (!hasPromotable) {
            return `A case must always keep at least one ${party.side}. Add another ${party.side} first, or replace this one.`
        }
        return ''
    }

    const plaintiffId = caseData?.plaintiff?.client_id
    const defendantId = caseData?.defendant?.client_id
    if (party.client_id != null) {
        if (party.client_id === plaintiffId || party.client_id === defendantId) {
            const clashSide = party.client_id === plaintiffId ? 'plaintiff' : 'defendant'
            return `${party.label} shares its client record (#${party.client_id}) with the primary ${nameOf(clashSide)}. Removing it would delete the primary party's record too, so the case blocks it. Give this party its own email address, or remove the primary ${nameOf(clashSide)} instead.`
        }
    }
    return ''
}
